/**
 * =============================================================================
 * IMAGE UPLOAD FAILURE NOTICE (shared — question + reply paths)
 * =============================================================================
 *
 * The recoverable partial-upload-failure panel (BL-WIDGET-UPLOAD-FAILURE-UX-001):
 * the entity (question or reply) was created and is pending review, but one or
 * more attached photos failed to upload. This is NOT a success screen — it is an
 * assertively-announced (`role="alert"`) state offering retry-failed-only and
 * continue-without actions, and the host never fires its `onSuccess` while here.
 *
 * Extracted so the hardened recovery UX lives in ONE tested place and the widget
 * bundle carries it once, not per form (BL-WIDGET-BUNDLE-BUDGET-001 — measured
 * smaller than two inline copies, whose distinct class namespaces don't gzip
 * -dedupe). Button styling reuses the shared `.fmp-btn` classes, exactly as each
 * host form's own buttons already do.
 *
 * =============================================================================
 */

import React, { useEffect, useRef } from 'react';
import { useStyleInjection } from '../hooks';
import type { FailedImageUpload } from '../lib/imageUpload';

export interface ImageUploadFailureNoticeProps {
  /**
   * Body summary (typically `buildPartialUploadMessage(...)`) — this carries the
   * subject-specific reassurance ("Your question/reply was submitted…"), so the
   * heading stays a single shared alert line.
   */
  message: string;
  /** The photos that failed to attach, listed by filename. */
  failed: FailedImageUpload[];
  /** Retry ONLY the failed photos against the already-created entity. */
  onRetry: () => void;
  /** Accept the submission without the failed photos (→ success). */
  onContinue: () => void;
  /** Whether a retry is in flight (disables both actions). */
  isRetrying: boolean;
}

/**
 * Shared partial-upload-failure panel for the question and reply forms.
 */
export function ImageUploadFailureNotice({
  message,
  failed,
  onRetry,
  onContinue,
  isRetrying,
}: ImageUploadFailureNoticeProps): React.ReactElement {
  useStyleInjection(imageUploadFailureNoticeStyles, 'fmp-upload-fail-styles');

  // Move focus to the panel on mount: the submitting form (incl. the focused
  // submit button) unmounts when this replaces it, so focus would otherwise fall
  // to <body> and a keyboard / screen-reader user would have to tab from the top
  // of the page to reach the recovery actions. role="alert" announces the text;
  // this makes the recovery reachable. Mirrors the forms' success/error focus.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  // Use aria-disabled (NOT the native `disabled` attribute) + an early-return
  // click guard so the Retry/Continue buttons stay FOCUSABLE while a retry is in
  // flight. A native `disabled` on the just-activated Retry button drops keyboard
  // focus to <body> for the multi-second retry, and the []-dep mount effect above
  // never re-focuses — so on a still-failed retry the user would be stranded on
  // <body>. Keeping focus on the button avoids that entirely; the polite live
  // region below announces "Retrying photos…" so the in-flight state isn't silent.
  const handleRetry = (): void => {
    if (isRetrying) return;
    onRetry();
  };
  const handleContinue = (): void => {
    if (isRetrying) return;
    onContinue();
  };

  return (
    <div className="fmp-upload-fail" role="alert" tabIndex={-1} ref={rootRef}>
      <svg
        className="fmp-upload-fail__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <h2 className="fmp-upload-fail__title">Some photos didn’t attach</h2>
      <p className="fmp-upload-fail__message">{message}</p>
      <ul className="fmp-upload-fail__list" aria-label="Photos that failed to upload">
        {failed.map((item) => (
          // Composite key: two selected files can share a name (same filename
          // from different folders / camera exports) — `name` alone would collide.
          <li key={`${item.name}-${item.file.size}-${item.file.lastModified}`}>{item.name}</li>
        ))}
      </ul>
      <button
        type="button"
        onClick={handleRetry}
        aria-disabled={isRetrying}
        className="fmp-btn fmp-btn--primary"
      >
        {isRetrying ? 'Retrying…' : 'Retry failed photos'}
      </button>
      <button
        type="button"
        onClick={handleContinue}
        aria-disabled={isRetrying}
        className="fmp-btn fmp-btn--secondary"
      >
        Continue without photos
      </button>
      {/* Polite live region: bounds the silence while a retry is in flight (focus
          stays on the Retry button, but a screen reader otherwise gets no cue that
          anything is happening during the multi-second upload). */}
      <p className="fmp-upload-fail__status" role="status" aria-live="polite">
        {isRetrying ? 'Retrying photos…' : ''}
      </p>
    </div>
  );
}

const imageUploadFailureNoticeStyles = `
  :where(.fmp-widget-scope) .fmp-upload-fail:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-upload-fail {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--fmp-space-section) var(--fmp-space-xl);
    gap: var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-upload-fail__icon {
    width: var(--fmp-size-48);
    height: var(--fmp-size-48);
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-upload-fail__title {
    margin: 0;
    font-size: var(--fmp-font-size-lg);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-upload-fail__message {
    max-inline-size: var(--fmp-layout-prose-max);
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-upload-fail__list {
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: var(--fmp-font-size-sm);
    color: var(--fmp-color-text-secondary);
    word-break: break-word;
  }

  /* aria-disabled keeps the button focusable (see the component note) but must
     still READ as disabled, since native :disabled styling no longer applies. */
  :where(.fmp-widget-scope) .fmp-upload-fail .fmp-btn[aria-disabled="true"] {
    opacity: 0.7;
    cursor: not-allowed;
  }

  /* Visually-hidden polite live region (screen-reader only). */
  :where(.fmp-widget-scope) .fmp-upload-fail__status {
    position: absolute;
    width: var(--fmp-size-1);
    height: var(--fmp-size-1);
    margin: calc(-1 * var(--fmp-size-1));
    padding: 0;
    border: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
  }
`;
